import { useCallback, useMemo, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { Tenant } from '@core/api/tenant';
import {
  type User,
  deleteUser,
  getAllUsers,
  resetUserPassword,
} from '@core/api/user';
import ConfirmDialog from '@core/components/dialogs/ConfirmDialog';
import PlainPasswordDialog from '@core/components/dialogs/PlainPasswordDialog';
import UserDialog from '@core/components/dialogs/UserDialog';
import { useAuth } from '@core/contexts/AuthContext';
import { ROLE_LABELS, canGrant } from '@core/enum/userRole';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import { useCrudList } from '@core/hooks/useCrudList';
import type { ContextMenuParams } from './ContextMenu';
import CrudListPage from './CrudListPage';

export interface UsersProps {
  loadItems?: () => Promise<User[]>;
  tenant?: Tenant | null;
}

const Users = ({ loadItems = getAllUsers, tenant }: UsersProps) => {
  const { user: currentUser } = useAuth();
  const [pwDialogUser, setPwDialogUser] = useState<User | null>(null);

  const {
    items: users,
    loading,
    openCreate,
    openEdit,
    dialogOpen,
    editingItem: editingUser,
    load: loadUsers,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    getRowId,
    findByRowId,
  } = useCrudList<User>({
    loadItems,
    deleteItem: (user) => deleteUser(user.id),
    loadErrorMessage: 'Failed to load users',
    deleteSuccessMessage: 'User deleted',
    deleteErrorMessage: 'Failed to delete user',
    getSearchHaystack: (user) =>
      [
        user.firstName,
        user.lastName,
        user.username,
        user.email,
        ROLE_LABELS[user.role],
      ].join(' '),
  });

  const handleUserCreated = useCallback((created: User) => {
    if (created.plainPassword) {
      setPwDialogUser(created);
    }
  }, []);

  const resetPasswordConfirm = useConfirmDialog<User>({
    onConfirm: async (user) => {
      const updated = await resetUserPassword(user.id);
      if (updated.plainPassword) {
        setPwDialogUser(updated);
      }
    },
    errorMessage: 'Failed to reset password',
  });

  const contextMenuItems = useCallback(
    ({ rowId }: ContextMenuParams) => {
      const user = findByRowId(rowId);
      if (!user || !canGrant(currentUser?.role, user.role)) {
        return [];
      }
      return [
        {
          label: 'Edit',
          onClick: () => {
            if (user) openEdit(user);
          },
        },
        {
          label: 'Reset password',
          onClick: () => {
            if (user) resetPasswordConfirm.request(user);
          },
        },
        {
          label: 'Delete',
          onClick: () => {
            if (user) requestDelete(user);
          },
        },
      ];
    },
    [
      currentUser?.role,
      findByRowId,
      openEdit,
      resetPasswordConfirm,
      requestDelete,
    ]
  );

  const columns = useMemo<GridColDef<User>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => `${row.firstName} ${row.lastName}`.trim(),
      },
      {
        field: 'username',
        headerName: 'Username',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 120,
        valueGetter: (_, row) => ROLE_LABELS[row.role],
      },
    ],
    []
  );

  return (
    <>
      <CrudListPage<User>
        variant="section"
        title="Users"
        rows={users}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        contextMenuItems={contextMenuItems}
        addConfig={{ label: 'Add User', onAdd: openCreate }}
        addEditDialog={
          <UserDialog
            open={dialogOpen}
            onClose={closeDialog}
            user={editingUser}
            tenant={tenant}
            onSuccess={() => loadUsers()}
            onUserCreated={handleUserCreated}
          />
        }
        deleteConfig={{
          title: 'Delete user',
          item: deleteConfirm,
          getContent: (user) =>
            `Are you sure you want to delete "${user.firstName} ${user.lastName}"?`,
          onConfirm: confirmDelete,
          onCancel: cancelDelete,
        }}
      />
      <ConfirmDialog
        open={resetPasswordConfirm.open}
        title="Reset password"
        content={
          resetPasswordConfirm.item
            ? `Generate a new password for "${resetPasswordConfirm.item.firstName} ${resetPasswordConfirm.item.lastName}"?`
            : ''
        }
        confirmText="Reset"
        cancelText="Cancel"
        onConfirm={resetPasswordConfirm.confirm}
        onCancel={resetPasswordConfirm.cancel}
        loading={resetPasswordConfirm.loading}
      />
      {pwDialogUser && (
        <PlainPasswordDialog
          open
          user={pwDialogUser}
          onClose={() => setPwDialogUser(null)}
        />
      )}
    </>
  );
};

export default Users;
